import { useCallback, useEffect, useId, useRef, useState } from "react";
import "../styles/footer-waitlist.css";

type Status = "idle" | "submitting" | "success" | "error";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const SUCCESS_MESSAGE = "Thanks — you're on the list. We'll be in touch shortly.";

type Delivery = { ok: true } | { ok: false; error: string };

type Signup = { name: string; email: string; company: string };

const UNDELIVERED_MESSAGE =
  "We could not record your signup right now. Please email jayden@spcprt.com directly.";

/**
 * Where signups go. The relay is a keyless form-to-email service; the browser
 * talks to it directly because requests that originate from a Cloudflare
 * Worker carry a cf-worker header that Cloudflare-fronted sites, this relay
 * included, refuse. A browser request carries none of that.
 *
 * After the destination address is activated the relay issues an opaque alias
 * URL; set VITE_WAITLIST_RELAY to it at build time to keep the address out of
 * the bundle.
 */
const RELAY_URL: string =
  import.meta.env.VITE_WAITLIST_RELAY || "https://formsubmit.co/ajax/jayden@spcprt.com";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "0.0.0.0"]);

/** Talk to the relay from the browser. Returns null to mean "fall back". */
async function sendViaRelay(signup: Signup): Promise<Delivery | null> {
  // Never let a dev server fire the relay's one-time activation mail, or a
  // real signup, at a real inbox. The function behind /api/waitlist has the
  // same guard, so local runs end in an honest ok:false either way.
  if (LOCAL_HOSTS.has(window.location.hostname) && !import.meta.env.VITE_WAITLIST_RELAY) {
    return null;
  }

  try {
    const response = await fetch(RELAY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...signup,
        _subject: `Spaceport waitlist: ${signup.name}`,
        _template: "table",
        _captcha: "false",
      }),
    });
    const payload = (await response.json()) as { success?: unknown; message?: unknown };
    const success =
      typeof payload.success === "boolean"
        ? payload.success
        : String(payload.success ?? "").toLowerCase() === "true";
    if (response.ok && success) return { ok: true };
    // The relay's own messages are written for the site operator, not the
    // visitor ("This form needs Activation..."), so do not surface them.
    return { ok: false, error: UNDELIVERED_MESSAGE };
  } catch {
    return null;
  }
}

/** Fallback: our own endpoint, which tries the relay server-side. */
async function sendViaFunction(signup: Signup): Promise<Delivery> {
  try {
    const response = await fetch("/api/waitlist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(signup),
    });
    let payload: { ok?: unknown; error?: unknown } = {};
    try {
      payload = (await response.json()) as typeof payload;
    } catch {
      payload = {};
    }
    if (response.ok && payload.ok === true) return { ok: true };
    return {
      ok: false,
      error: typeof payload.error === "string" ? payload.error : UNDELIVERED_MESSAGE,
    };
  } catch {
    return { ok: false, error: "Network error. Please check your connection and try again." };
  }
}

export function Footer() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const wantsFocusRef = useRef(false);

  const fieldId = useId();
  const nameId = `${fieldId}-name`;
  const emailId = `${fieldId}-email`;
  const companyId = `${fieldId}-company`;
  const panelId = `${fieldId}-panel`;

  const openForm = useCallback(() => {
    wantsFocusRef.current = true;
    setOpen(true);
  }, []);

  // Cross-component contract: the hero CTA opens this form.
  useEffect(() => {
    const handler = () => openForm();
    window.addEventListener("spaceport:open-waitlist", handler);
    return () => window.removeEventListener("spaceport:open-waitlist", handler);
  }, [openForm]);

  // Move focus to the first field once the panel is actually expanded.
  useEffect(() => {
    if (!open || !wantsFocusRef.current) return;
    wantsFocusRef.current = false;
    if (status === "success") return;
    const frame = window.requestAnimationFrame(() => nameRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open, status]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "submitting") return;

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setStatus("error");
      setMessage("Please enter your name.");
      nameRef.current?.focus();
      return;
    }

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      emailRef.current?.focus();
      return;
    }

    setStatus("submitting");
    setMessage("Sending...");

    const payload = { name: trimmedName, email: trimmedEmail, company: company.trim() };
    const delivered = (await sendViaRelay(payload)) ?? (await sendViaFunction(payload));

    if (delivered.ok) {
      setStatus("success");
      setMessage(SUCCESS_MESSAGE);
    } else {
      setStatus("error");
      setMessage(delivered.error);
    }
  }

  const submitting = status === "submitting";
  const succeeded = status === "success";

  return (
    <footer className="site-footer" id="contact">
      <div className="footer-panel">
        <img src="/assets/SpaceportIcons/SpaceportFullLogoWhite.svg" alt="Spaceport" />

        <div className="waitlist">
          <div className="waitlist__collapse" data-open={open ? "false" : "true"}>
            <div className="waitlist__collapse-inner">
              <button
                type="button"
                className="waitlist__toggle"
                aria-expanded={open}
                aria-controls={panelId}
                tabIndex={open ? -1 : 0}
                onClick={openForm}
              >
                <span>Join waitlist</span>
              </button>
            </div>
          </div>

          <div
            className="waitlist__collapse"
            id={panelId}
            data-open={open ? "true" : "false"}
            aria-hidden={!open}
          >
            <div className="waitlist__collapse-inner">
              {succeeded ? (
                <p className="waitlist__thanks">{SUCCESS_MESSAGE}</p>
              ) : (
                <form className="waitlist__form" onSubmit={handleSubmit} noValidate>
                  <div className="waitlist__row">
                    <label className="waitlist__label" htmlFor={nameId}>
                      Name
                    </label>
                    <input
                      id={nameId}
                      ref={nameRef}
                      className="waitlist__input"
                      type="text"
                      name="name"
                      placeholder="Name"
                      autoComplete="name"
                      required
                      maxLength={200}
                      disabled={submitting}
                      tabIndex={open ? 0 : -1}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />

                    <label className="waitlist__label" htmlFor={emailId}>
                      Email
                    </label>
                    <input
                      id={emailId}
                      ref={emailRef}
                      className="waitlist__input"
                      type="email"
                      name="email"
                      placeholder="Email"
                      autoComplete="email"
                      required
                      maxLength={200}
                      disabled={submitting}
                      tabIndex={open ? 0 : -1}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className="waitlist__row">
                    <label className="waitlist__label" htmlFor={companyId}>
                      Company (optional)
                    </label>
                    <input
                      id={companyId}
                      className="waitlist__input"
                      type="text"
                      name="company"
                      placeholder="Company (optional)"
                      autoComplete="organization"
                      maxLength={200}
                      disabled={submitting}
                      tabIndex={open ? 0 : -1}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />

                    <button
                      type="submit"
                      className="waitlist__submit"
                      disabled={submitting}
                      tabIndex={open ? 0 : -1}
                    >
                      Join waitlist
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <p
            className="waitlist__status"
            data-tone={status}
            role="status"
            aria-live="polite"
          >
            {message}
          </p>
        </div>
      </div>

      <div className="footer-bottom">
        <span>© 2026 Spaceport Labs, Inc.</span>
      </div>
    </footer>
  );
}
